import * as React from 'react';
import { useState, useEffect } from 'react';
import ManageAccountSetup from '../../components/account/manage-account-setup';
import { ListMenu } from '@enginuity-apps-ui-components/molecules/list-menu/list-menu';
import ManageAccountOptIns from '../../components/account/manage-account-optins';
import ManageAccountPersonalDetails from '../../components/account/manage-account-personal-details';
import ManageAccountEmploymentDetails from '../../components/account/manage-account-employment-details';
import ManageAccountPassword from '../../components/account/manage-account-password';
import ManageAccountDelete, { IDeleteAccountFormData } from '../../components/account/manage-account-delete';
import { OptionsType } from 'react-select';
import { UserService } from '@enginuity-common/services/userService';
import { IChangePasswordFormData } from '../../components/account/manage-account-password';
import { IUserProfile } from '@enginuity-common/types/user';
import { SpinnerOverlay } from '@enginuity-apps-ui-components/atoms/spinner/spinner';
import GenericErrorMessage from '../../components/misc/generic-error-message';
import { mapUser } from '@enginuity-common/utils/auth';
import CheckEmail from '../../components/misc/check-email';
import ConfirmAccountDelete from './confirm-account-delete';
import { isSignedIn, redirectUserForAuthentication } from '@enginuity-common/utils/auth';

export type IManageAccountProps = {
  userProfile?: IUserProfile;
  onProfileChange: (data: IUserProfile) => void;
  fromUri: string;
};

export type IAccountManagmentFormOptions = {
  qualification: OptionsType<any>[];
  ageRange: OptionsType<any>[];
  sector: OptionsType<any>[];
  employmentStatus: {
    [id: string]: { label: JSX.Element; value: any; };
  };
};

export type ISubmitStatus = {
  result: boolean;
  error: JSX.Element | undefined;
};

export default function ManageAccount({ userProfile, onProfileChange, fromUri }: IManageAccountProps) {
  const [genericError, setGenericError] = useState<string | undefined>();
  const [showSpinner, setShowSpinner] = useState(!!userProfile);
  const [accountManagementFormOptions, setAccountManagementFormOptions] = useState<IAccountManagmentFormOptions | null>(null);
  const [showEmailSentConfirmation, setShowEmailSentConfirmation] = useState<string | undefined>();
  const [showAccountDeleteConfirmation, setShowAccountDeleteConfirmation] = useState<string | undefined>();

  const usersService = new UserService({
    baseUrl: window.spConfig.usersBaseUrl
  });

  const updateUserProfile = (data: any): Promise<ISubmitStatus> => {
    setShowSpinner(true);
    // update user's profile
    return usersService
      .updateUser(data)
      .then(response => {
        setShowSpinner(false);
        return response.json().then((body: any) => {
          return {
            body: body,
            status: response.status
          }
        })
      })
      .then(data => {
        switch (data.status) {
          case 200:
            const user = mapUser(data.body);
            onProfileChange(user.profile);
            return { result: true, error: undefined }
          case 400:
            return { result: false, error: <>A problem occured with the request. Please try again</> };
          case 403:
            return { result: false, error: <>{data.body}</> }
        }
        throw new Error('An error occurred while updating user details.');
      })
      .catch(error => {
        console.log(error);
        setShowSpinner(false);
        setGenericError(error);
        return { result: false, error: error }
      });
  }

  const updateEmailAddress = (email: string): Promise<ISubmitStatus> => {
    setShowSpinner(true);
    // update user's profile
    return usersService
      .changeEmail({ newEmail: email })
      .then(response => {
        setShowSpinner(false);
        return response.json().then((body: any) => {
          return {
            body: body,
            status: response.status
          }
        })
      })
      .then(data => {
        switch (data.status) {
          case 200:
            const user = mapUser(data);
            onProfileChange(user.profile);
            setShowEmailSentConfirmation(email);
            return { result: true, error: undefined }
          case 400:
            return { result: false, error: <>A problem occured with the request. Please try again</> };
          case 403:
            return { result: false, error: <>{data.body}</> }
          case 409:
            return { result: false, error: <>An error occurred while attempting to update the email. This email may already be is use.</> }
        }
        throw new Error('An error occurred while attempting to update the email.');
      })
      .catch(error => {
        console.log(error);
        setShowSpinner(false);
        setGenericError(error);
        return { result: false, error: error }
      });

  }

  const onUserProfileFormDataSubmit = async (data: IUserProfile): Promise<ISubmitStatus> => {
    let newEmail;
    if (data.email) {
      if (data.email.toUpperCase() !== userProfile?.email.toUpperCase()) {
        newEmail = data.email;
      };
      delete data.email;
    }
    let result = await updateUserProfile(data);

    if (newEmail) {
      result = await updateEmailAddress(newEmail);
    }

    return result;
  };


  const onChangePassword = async (data: IChangePasswordFormData): Promise<ISubmitStatus> => {
    setShowSpinner(true);

    const result = await usersService
      .changePassword({ oldPassword: data.currentPassword, newPassword: data.newPassword })

      .then(response => {
        return response.json().then((body: any) => {
          return {
            body: body,
            status: response.status
          }
        })
      })
      .then((data) => {
        setShowSpinner(false);
        switch (data.status) {
          case 200:
            return { result: true, error: undefined }
          case 400:
            return { result: false, error: <>A problem occured with the request. Please try again</> };
          case 403:
            return { result: false, error: <>{data.body}</> }
        }
        throw new Error('An error occurred while updating user details.');
      })
      .catch(error => {
        console.log(error);
        setShowSpinner(false);
        setGenericError(error);
        return { result: false, error: error }
      });

    return result;
  };

  const onDeleteAccount = async (data: IDeleteAccountFormData): Promise<ISubmitStatus> => {
    // setShowSpinner(true);
    console.log("Delete account not implemented yet");
    //TODO Call account delete api and logout
    // await usersService
    //   .deleteAccount({ password: data.currentPassword })
    //   .then(response => {
    //     setShowSpinner(false);
    //     if (response.status === 400) {
    //       return Promise.resolve(null);
    //     } else if (!response.ok) {
    //       throw new Error('An error occurred while updating user details.');
    //     }
    //   })
    //   .catch(error => {
    //     console.log(error);
    //     setShowSpinner(false);
    //     setGenericError(error);
    //   });
    setShowAccountDeleteConfirmation(userProfile?.email);
    return { result: true, error: undefined };
  };

  const convertIncomingFormOption = (incoming: string) => ({
    value: incoming,
    label: incoming,
  });


  const convertEmploymentStatusOptions = (opts: any[]): { [id: string]: { label: JSX.Element; value: any; }; } => {
    const newOpts: any = {};
    opts.forEach((incoming: string) => {
      newOpts[incoming] = {
        value: incoming,
        label: <>{incoming}</>,
      }
    })

    return newOpts;
  };

  const getOptionsData = async () => {
    await usersService.getEngageOnboardingData()
      .then(response => {
        if (!response.ok) {
          throw new Error('An error occurred while updating retrieving onboarding options.');
        }
        return response.json();
      })
      .then(data => {
        setAccountManagementFormOptions({
          qualification: data.qualificationOptions.map(convertIncomingFormOption),
          ageRange: data.ageRangeOptions.map(convertIncomingFormOption),
          sector: data.sectorOptions.map(convertIncomingFormOption),
          employmentStatus: convertEmploymentStatusOptions(data.employmentStatusOptions),
        });
      }).catch(error => {
        console.log(error);
        setShowSpinner(false);
        setGenericError(error);
      });
  };

  const getProfile = async () => {
    // get user's profile
    await usersService
      .getUserProfile()
      .then(response => {
        if (!response.ok) {
          throw new Error('An error occurred while fetching user details.');
        }

        return response.json();
      })
      .then((data: any) => {
        const user = mapUser(data);
        onProfileChange(user.profile);
      })
      .catch(error => {
        setShowSpinner(false);
      });
  };

  const loadData = async () => {
    // get user's profile
    await Promise.all([getProfile(), getOptionsData()]);
  };

  useEffect(() => {
    if (isSignedIn()) {
      setShowSpinner(true)
      loadData().then(() => {
        setShowSpinner(false);
      }
      ).catch(error => {
        console.log(error);
        setShowSpinner(false);
        setGenericError(error);
      });
    } else {
      redirectUserForAuthentication();
    }
  }, []);

  if (genericError) {
    return <GenericErrorMessage errorMsg={genericError} tryAgainLink="/manage" fromUri={fromUri}></GenericErrorMessage>
  }

  if (!userProfile) {
    return <><SpinnerOverlay active={showSpinner} /></>;
  }

  const menuOptions = {
    'account-setup': { title: "01. Account setup", link: "anchor-account-setup" },
    'account-options': { title: "02. Opt ins", link: "anchor-account-options" },
    'account-personal': { title: "03. Personal details", link: "anchor-account-personal" },
    'account-employment': { title: "04. Employment details", link: "anchor-account-employment" },
    'account-password': { title: "05. Password", link: "anchor-account-password" },
    'account-delete': { title: "06. Delete my account", link: "anchor-account-delete" }
  };

  if (showEmailSentConfirmation) {
    return <CheckEmail email={showEmailSentConfirmation} fromUri={fromUri} ><p>Click the link in the email to confirm your new address</p></CheckEmail>;
  }

  if (showAccountDeleteConfirmation) {
    return <ConfirmAccountDelete email={showAccountDeleteConfirmation}></ConfirmAccountDelete>;
  }

  return (<>
    <div className="container">
      <div className="row md-spacer-80 spacer-32">
        <div className="col-lg-12">
          <h1>My account</h1>
        </div>
      </div>
      <div className="row spacer-48">
        <div className="col-lg-1">
        </div>
        <div className="col-lg-11">
          <ListMenu list={menuOptions}></ListMenu>
        </div>
      </div>
      <div className="row spacer-64">
        <div className="col-lg-1">
        </div>
        <div className="col-lg-11">
          <div id="anchor-account-setup" className="element">
            <ManageAccountSetup onSubmit={onUserProfileFormDataSubmit} data={userProfile}></ManageAccountSetup>
          </div>
        </div>
      </div>
      <div className="row spacer-48">
        <div className="col-lg-1">
        </div>
        <div className="col-lg-11">
          <div id="anchor-account-options" className="element">
            <ManageAccountOptIns onSubmit={onUserProfileFormDataSubmit} data={userProfile}></ManageAccountOptIns>
          </div>
        </div>
      </div>
      <div className="row spacer-48">
        <div className="col-lg-1">
        </div>
        <div className="col-lg-11">
          <div id="anchor-account-personal" className="element">
            <ManageAccountPersonalDetails onSubmit={onUserProfileFormDataSubmit} data={userProfile} options={accountManagementFormOptions}></ManageAccountPersonalDetails>
          </div>
        </div>
      </div>
      <div className="row spacer-48">
        <div className="col-lg-1">
        </div>
        <div className="col-lg-11">
          <div id="anchor-account-employment" className="element">
            <ManageAccountEmploymentDetails onSubmit={onUserProfileFormDataSubmit} data={userProfile} options={accountManagementFormOptions}></ManageAccountEmploymentDetails>
          </div>
        </div>
      </div>
      <div className="row spacer-48" id="goto">
        <div className="col-lg-1">
        </div>
        <div className="col-lg-11">
          <div id="anchor-account-password" className="element">
            <ManageAccountPassword onSubmit={onChangePassword} email={userProfile?.email || ""}></ManageAccountPassword>
          </div>
        </div>
      </div>
      <div className="row spacer-48">
        <div className="col-lg-1">
        </div>
        <div className="col-lg-11">
          <div id="anchor-account-delete" className="element">
            <ManageAccountDelete onSubmit={onDeleteAccount}></ManageAccountDelete>
          </div>
        </div>
      </div>
      <div className="row spacer-80">
        <div className="col-lg-12">
          &nbsp;
        </div>
      </div>
    </div >
    <SpinnerOverlay active={showSpinner} />
  </>
  );

}
